import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TextBox, SelectBox, Button } from "devextreme-react";
import HtmlEditor, { Item, Toolbar as HTMLToolbar } from "devextreme-react/html-editor";
import {
    Ticket, TicketStatus, TicketPriority, SatisfactionSurvey, isClosedStatus, TICKET_STATUS_DEFS,
} from "../../../api/support";
import {
    PRIORITY_TEXT_KEYS, PRODUCT_TEXT, formatDate, getInitials, getSlaInfo, formatSlaRemaining,
} from "./ticket-constants";

interface GeneralTabProps {
    ticket: Ticket;
    survey?: SatisfactionSurvey | null;
    editMode?: boolean;
    saving?: boolean;
    onEdit?: () => void;
    onSave?: (patch: Partial<Ticket>) => void;
    onCancel?: () => void;
}

// Edit modundaki secenekler (mevcut sabitlerden turetilir).
const PRODUCT_OPTIONS = Object.keys(PRODUCT_TEXT).map(code => ({ code: Number(code), key: PRODUCT_TEXT[Number(code)] }));
const PRIORITY_OPTIONS = ([1, 2, 3] as TicketPriority[]).map(p => ({ value: p, key: PRIORITY_TEXT_KEYS[p] }));
const STATUS_OPTIONS = TICKET_STATUS_DEFS.filter(d => d.isActive);

export function GeneralTab({ ticket, survey, editMode, saving, onEdit, onSave, onCancel }: GeneralTabProps) {
    const { t } = useTranslation();

    // Duzenlenebilir alanlarin yerel durumu — ticket/edit moduna girince tazelenir.
    const [title, setTitle] = useState(ticket.title);
    const [description, setDescription] = useState(ticket.description ?? "");
    const [priority, setPriority] = useState<TicketPriority>(ticket.priority);
    const [productCode, setProductCode] = useState<number>(ticket.product_code);
    const [status, setStatus] = useState<TicketStatus>(ticket.status);

    useEffect(() => {
        setTitle(ticket.title);
        setDescription(ticket.description ?? "");
        setPriority(ticket.priority);
        setProductCode(ticket.product_code);
        setStatus(ticket.status);
    }, [ticket, editMode]);

    const ro = !editMode; // readOnly: duzenlenebilir alanlar yalnizca edit modunda acilir
    const hasCustomer = !!(ticket.customer_name || ticket.customer_email);

    return (
        <div className="p-3 sm:p-4 h-full overflow-auto flex flex-col">
            {/* Sag ust: Duzenle / (edit modunda) Kaydet + Iptal */}
            <div className="flex shrink-0" style={{ justifyContent: "flex-end", gap: 8, marginBottom: 12 }}>
                {editMode ? (
                    <>
                        <Button
                            text={t("save")}
                            icon="save"
                            type="default"
                            stylingMode="contained"
                            disabled={saving || !title.trim()}
                            onClick={() => onSave?.({ title: title.trim(), description, priority, product_code: productCode, status })}
                        />
                        <Button text={t("cancel")} icon="close" type="normal" stylingMode="contained" disabled={saving} onClick={onCancel} />
                    </>
                ) : (
                    <Button text={t("edit")} icon="edit" type="default" stylingMode="contained" onClick={onEdit} />
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {/* Column 1 - Ticket Bilgileri (duzenlenebilir) */}
                <div className="rounded-lg p-5 shadow-sm flex flex-col ticket-card">
                    <h4 className="text-sm font-semibold mb-4 pb-2 ticket-card-title">{t("ticketInfo")}</h4>
                    <div className="space-y-3">
                        <FieldBox label={t("ticketNo")}>
                            <TextBox value={ticket.ticket_code} readOnly stylingMode="outlined" width="100%" />
                        </FieldBox>
                        <FieldBox label={t("title")}>
                            <TextBox value={title} readOnly={ro} maxLength={251} stylingMode="outlined" width="100%"
                                onValueChanged={e => setTitle(e.value ?? "")} />
                        </FieldBox>
                        <FieldBox label={t("product")}>
                            <SelectBox dataSource={PRODUCT_OPTIONS} value={productCode} valueExpr="code"
                                displayExpr={(o: any) => o ? t(o.key) : ""} readOnly={ro} stylingMode="outlined" width="100%"
                                onValueChanged={e => setProductCode(Number(e.value))} />
                        </FieldBox>
                        <FieldBox label={t("status")}>
                            <SelectBox dataSource={STATUS_OPTIONS} value={status} valueExpr="nr"
                                displayExpr={(d: any) => d ? t(d.labelKey) : ""} readOnly={ro} stylingMode="outlined" width="100%"
                                onValueChanged={e => setStatus(Number(e.value) as TicketStatus)} />
                        </FieldBox>
                        <FieldBox label={t("priority")}>
                            <SelectBox dataSource={PRIORITY_OPTIONS} value={priority} valueExpr="value"
                                displayExpr={(o: any) => o ? t(o.key) : ""} readOnly={ro} stylingMode="outlined" width="100%"
                                onValueChanged={e => setPriority(Number(e.value) as TicketPriority)} />
                        </FieldBox>
                        <FieldBox label={t("ticketType")}>
                            <TextBox value={ticket.type_name || "—"} readOnly stylingMode="outlined" width="100%" />
                        </FieldBox>
                        <FieldBox label={t("supportGroup")}>
                            <TextBox value={ticket.support_group_name || "—"} readOnly stylingMode="outlined" width="100%" />
                        </FieldBox>
                        <FieldBox label={t("sla")}>
                            {(() => {
                                const sla = getSlaInfo(ticket);
                                if (sla.state === "none") return <span className="text-sm" style={{ color: 'var(--subtitle-text-color)' }}>—</span>;
                                const rem = formatSlaRemaining(sla.remainingMs as number, t);
                                const label = sla.state === "overdue" ? t("slaOverdueBy", { time: rem }) : t("slaRemaining", { time: rem });
                                return (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: `${sla.color}1f`, color: sla.color, fontWeight: 600, fontSize: 12, padding: '4px 10px', borderRadius: 8 }}>
                                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: sla.color }} />
                                        {label}
                                    </span>
                                );
                            })()}
                        </FieldBox>
                    </div>
                </div>

                {/* Column 2 - Musteri Bilgileri (salt-okunur) */}
                <div className="rounded-lg p-5 shadow-sm flex flex-col ticket-card">
                    <h4 className="text-sm font-semibold mb-4 pb-2 ticket-card-title">{t("customerInfo")}</h4>
                    <div className="space-y-3">
                        {hasCustomer ? (
                            <>
                                <FieldBox label={t("customer")}>
                                    <TextBox value={ticket.customer_name || "—"} readOnly stylingMode="outlined" width="100%" />
                                </FieldBox>
                                <FieldBox label={t("email")}>
                                    <TextBox value={ticket.customer_email || "—"} readOnly stylingMode="outlined" width="100%" />
                                </FieldBox>
                            </>
                        ) : (
                            <div className="flex items-start gap-2 rounded-lg p-3" style={{ background: '#F59E0B14', border: '1px solid #F59E0B55' }}>
                                <span style={{ fontSize: 16, lineHeight: 1 }}>⚠</span>
                                <div>
                                    <div className="text-sm font-semibold" style={{ color: '#B45309' }}>{t("customerUnassigned")}</div>
                                    <div className="text-xs mt-0.5" style={{ color: 'var(--subtitle-text-color)' }}>{t("customerUnassignedDesc")}</div>
                                </div>
                            </div>
                        )}
                        <FieldBox label={t("assignedTo")}>
                            {ticket.assigned_to_name ? (
                                <div className="flex items-center gap-2 pt-1">
                                    <div className="w-6 h-6 bg-gradient-to-br from-slate-600 to-slate-800 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                                        {getInitials(ticket.assigned_to_name)}
                                    </div>
                                    <span className="text-sm font-medium" style={{ color: 'var(--base-text-color)' }}>{ticket.assigned_to_name}</span>
                                </div>
                            ) : (
                                <TextBox value={t("unassigned")} readOnly stylingMode="outlined" width="100%" />
                            )}
                        </FieldBox>
                    </div>
                </div>

                {/* Column 3 - Tarih Bilgileri & Aciklama */}
                <div className="rounded-lg p-5 shadow-sm flex flex-col ticket-card">
                    <h4 className="text-sm font-semibold mb-4 pb-2 ticket-card-title">{t("dateInfo")}</h4>
                    <div className="space-y-3">
                        <FieldBox label={t("createdAt")}>
                            <TextBox value={formatDate(ticket.created_at, true)} readOnly stylingMode="outlined" width="100%" />
                        </FieldBox>
                        {ticket.first_responded_at && (
                            <FieldBox label={t("firstResponse")}>
                                <TextBox value={formatDate(ticket.first_responded_at)} readOnly stylingMode="outlined" width="100%" />
                            </FieldBox>
                        )}
                        {ticket.due_date && (
                            <FieldBox label={t("dueDate")}>
                                <TextBox value={formatDate(ticket.due_date)} readOnly stylingMode="outlined" width="100%" />
                            </FieldBox>
                        )}
                        {ticket.closed_at && (
                            <FieldBox label={t("closed")}>
                                <TextBox value={formatDate(ticket.closed_at)} readOnly stylingMode="outlined" width="100%" />
                            </FieldBox>
                        )}
                    </div>
                    <div className="mt-3">
                        <label className="block text-xs mb-1 ticket-label">{t("description")}</label>
                        {editMode ? (
                            <div style={{ border: '1px solid var(--border-color)', borderRadius: 6, overflow: 'hidden', height: 180 }}>
                                <HtmlEditor height="100%" value={description} onValueChanged={e => setDescription(e.value)}>
                                    <HTMLToolbar>
                                        <Item name="undo" /><Item name="redo" /><Item name="separator" />
                                        <Item name="bold" /><Item name="italic" /><Item name="underline" />
                                        <Item name="separator" />
                                        <Item name="orderedList" /><Item name="bulletList" />
                                        <Item name="separator" /><Item name="link" />
                                    </HTMLToolbar>
                                </HtmlEditor>
                            </div>
                        ) : (
                            <div
                                className="text-sm leading-relaxed rounded p-2 ticket-description"
                                style={{ border: '1px solid var(--border-color)', minHeight: 40 }}
                                dangerouslySetInnerHTML={{ __html: ticket.description || "—" }}
                            />
                        )}
                    </div>
                </div>

                {/* Column 4 - Memnuniyet Anketi (sadece kapali ticketlarda) */}
                {isClosedStatus(ticket.status) && (
                    <div className="rounded-lg p-5 shadow-sm flex flex-col ticket-card">
                        <h4 className="text-sm font-semibold mb-4 pb-2 ticket-card-title">
                            {t("surveyTitle")}
                        </h4>
                        {survey ? (
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs mb-1.5 ticket-label">{t("surveyScore")}</label>
                                    <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map(star => (
                                            <svg
                                                key={star}
                                                className={`w-5 h-5 ${star <= survey.score ? "text-amber-400" : "text-gray-200"}`}
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                            </svg>
                                        ))}
                                        <span className="text-sm font-semibold ml-1.5" style={{ color: 'var(--base-text-color)' }}>
                                            {survey.score}/5
                                        </span>
                                    </div>
                                </div>
                                {survey.feedback && (
                                    <div>
                                        <label className="block text-xs mb-1 ticket-label">{t("surveyFeedbackLabel")}</label>
                                        <p className="text-sm leading-relaxed rounded p-2 ticket-description">
                                            {survey.feedback}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-xs mb-1 ticket-label">{t("surveyDate")}</label>
                                    <span className="text-sm block ticket-value-secondary">{formatDate(survey.created_at)}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-6" style={{ color: 'var(--subtitle-text-color)' }}>
                                <svg className="w-8 h-8 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs font-medium">{t("surveyNotResponded")}</p>
                                <p className="text-xs mt-0.5">{t("surveyNotRespondedDesc")}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
}

function FieldBox({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <label className="block text-xs mb-1 ticket-label">{label}</label>
            {children}
        </div>
    );
}
